import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import { useUserContext } from '../Components/StateContext/UserContext';

// Editable copy of one exam participant. DateOfBirth is free-text in Norwegian dd.MM.yyyy form.
interface EditableExamRow {
  Id: number;
  Firstname: string;
  Lastname: string;
  FirstnameTamil: string;
  LastnameTamil: string;
  DateOfBirth: string;
}

// ISO "2015-06-01T00:00:00" → "01.06.2015" for the free-text input.
const isoToNo = (iso?: string): string => {
  if (!iso) return '';
  const p = iso.slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : '';
};

const toRow = (p: Api.ExamRegistration): EditableExamRow => ({
  Id: p.Id,
  Firstname: p.Firstname || '',
  Lastname: p.Lastname || '',
  FirstnameTamil: p.FirstnameTamil || '',
  LastnameTamil: p.LastnameTamil || '',
  DateOfBirth: isoToNo(p.DateOfBirth),
});

// One field "cell": small label above the input, fixed-ish width so rows wrap tidily.
const fieldStyle: React.CSSProperties = { flex: '1 1 9rem', minWidth: '8rem' };

const EditExamParticipantsView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const canEdit = userRoles.includes('localadmin') || userRoles.includes('admin');

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<EditableExamRow[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!canEdit) return;
    (async () => {
      try {
        setIsLoading(true);
        const data = await RestService.get<Api.ExamRegistration[]>('/api/examregistration/ParticipantsForEditing');
        setRows((data || []).map(toRow));
      } catch (e) {
        toast.error(`Kunne ikke laste deltakere: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [canEdit]);

  const updateField = (id: number, field: keyof EditableExamRow, value: string) =>
    setRows(rs => rs.map(r => (r.Id === id ? { ...r, [field]: value } : r)));

  const save = async (row: EditableExamRow) => {
    if (!row.Firstname.trim() || !row.Lastname.trim()) {
      toast.error('Fornavn og etternavn er påkrevd.');
      return;
    }
    setSavingId(row.Id);
    try {
      await RestService.post<any, { Message: string }>('/api/examregistration/UpdateParticipant', {
        Id: row.Id,
        Firstname: row.Firstname.trim(),
        Lastname: row.Lastname.trim(),
        FirstnameTamil: row.FirstnameTamil.trim() || null,
        LastnameTamil: row.LastnameTamil.trim() || null,
        DateOfBirth: row.DateOfBirth.trim() || null,
      });
      toast.success('Lagret.');
    } catch (e) {
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      `${r.Firstname} ${r.Lastname} ${r.FirstnameTamil} ${r.LastnameTamil}`.toLowerCase().includes(q));
  }, [rows, query]);

  if (!canEdit) {
    return (
      <>
        <OKomBreadcrumb path='Påmelding, Endre påmeldinger eksamen' />
        <p className="ods-text--align-center ods-margin-top-5">Du har ikke tilgang til denne siden.</p>
      </>
    );
  }

  return (
    <>
      <OKomBreadcrumb path='Påmelding, Endre påmeldinger eksamen' />
      {isLoading && <OKomLoading text='Henter deltakere …' />}

      <div className="ods-margin-top-5" style={{ padding: '0 1.5rem' }}>
        <h1 className='ods-text--size-jumbo'>Endre påmeldinger – eksamen</h1>

        <div className="ods-search-inline ods-margin-top-3" style={{ maxWidth: '28rem' }}>
          <label className="ods-search-inline__label">
            <input
              placeholder='Søk på navn'
              className="ods-search-inline__input"
              type="search"
              autoComplete="off"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>
        </div>

        <div className="ods-margin-top-3">
          {filtered.map(row => (
            <div
              key={row.Id}
              style={{
                display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap',
                padding: '0.75rem 0', borderBottom: '1px solid #e0e0e0',
              }}
            >
              <label className="ods-input__label" style={fieldStyle}>Fornavn
                <input className="ods-input__input" value={row.Firstname} onChange={e => updateField(row.Id, 'Firstname', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Etternavn
                <input className="ods-input__input" value={row.Lastname} onChange={e => updateField(row.Id, 'Lastname', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Fornavn (tamil)
                <input className="ods-input__input" value={row.FirstnameTamil} onChange={e => updateField(row.Id, 'FirstnameTamil', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Etternavn (tamil)
                <input className="ods-input__input" value={row.LastnameTamil} onChange={e => updateField(row.Id, 'LastnameTamil', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Fødselsdato
                <input className="ods-input__input" placeholder="dd.mm.åååå" value={row.DateOfBirth} onChange={e => updateField(row.Id, 'DateOfBirth', e.target.value)} />
              </label>
              <button type="button" className="ods-button" style={{ flex: '0 0 auto' }} onClick={() => save(row)} disabled={savingId === row.Id}>
                Lagre{savingId === row.Id && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={6} /></span>}
              </button>
            </div>
          ))}
          {filtered.length === 0 && !isLoading && (
            <p className="ods-text--align-center ods-color-text-grayscale-10" style={{ padding: '1rem' }}>
              {rows.length === 0 ? 'Ingen deltakere i din avdeling.' : 'Ingen treff.'}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default withRouter(EditExamParticipantsView);
